import React, { ReactNode } from "react"
import { View, ViewStyle, StyleProp, Role } from "react-native"

import styled from "styled-components/native"

import { MediaImageFile, MediaUrl } from "@treefort/api-spec"
import tokens from "@treefort/tokens/app"
import icons from "@treefort/tokens/app/icons"

import { kebabCase } from "../lib/kebab-case"
import { i18nKey } from "../types/i18next"
import IconButton from "./icon-button"
import { SquareMediaThumbnail } from "./thumbnail"
import ThumbnailWithTitle from "./thumbnail-with-title"
import Touchable from "./touchable"

const FLEX_STYLE = { flex: 1 }

export interface ThumbnailListItemProps {
  children?: React.ReactNode
  showActionButton?: boolean
  role?: Role
  data?: {
    artworkMedia?:
      | MediaImageFile
      | MediaUrl
      | Array<MediaImageFile | MediaUrl | undefined>
    showLockIcon?: boolean
    title: string
    subtitle?: string
    displayTypeStringKey?: i18nKey
    onPress?: () => void
    actionButton?: {
      label: string
      onPress: () => void
    }
  }
}

const Container = styled(ConditionalTouchable)<{
  $showActionButton: boolean | undefined
}>`
  flex-direction: row;
  align-items: center;
  padding-left: ${(props) => props.theme.spacing.pagePaddingHorizontal}px;
  padding-right: ${(props) =>
    props.$showActionButton
      ? props.theme.spacing.xsmall
      : props.theme.spacing.pagePaddingHorizontal}px;
  padding-vertical: ${(props) =>
    props.theme.thumbnailListItem.paddingVertical}px;
`

const ContentContainer = styled.View`
  flex: 1;
  padding-left: ${(props) => props.theme.spacing.medium}px;
`

function ConditionalTouchable({
  id,
  children,
  onPress,
  style,
}: {
  id: string
  children: ReactNode
  onPress?: () => void
  style?: StyleProp<ViewStyle>
}): React.JSX.Element {
  const view = <View style={style}>{children}</View>
  if (onPress) {
    return (
      <Touchable
        id={id}
        onPress={onPress}
        feedback="ripple-or-highlight"
        style={FLEX_STYLE}
        containerStyle={FLEX_STYLE}
      >
        {view}
      </Touchable>
    )
  } else {
    return view
  }
}

/**
 * Renders a list item with a thumbnail on the left and metadata to the right
 * and an optional meatball menu button. Can take arbitrary children which will
 * be rendered as the last row of metadata.
 */
export default function ThumbnailListItem({
  data,
  showActionButton,
  role,
  children = null,
}: ThumbnailListItemProps): React.JSX.Element {
  return (
    <Container
      id={kebabCase(
        `thumbnail-list-item-artwork-${data?.title || "unknown-title"}`,
      )}
      onPress={data?.onPress}
      aria-role={data ? role : undefined}
      aria-label={data?.title}
      $showActionButton={showActionButton}
    >
      <SquareMediaThumbnail
        media={data?.artworkMedia}
        size={tokens.thumbnailListItem.artwork.height}
        showLockIcon={data?.showLockIcon}
      />
      <ContentContainer>
        <ThumbnailWithTitle
          titleNumberOfLines={children ? 1 : 2}
          data={
            data
              ? {
                  title: data.title,
                  subtitle: data.subtitle,
                  displayTypeStringKey: data.displayTypeStringKey,
                }
              : undefined
          }
        />
        {children}
      </ContentContainer>
      {showActionButton && (
        <IconButton
          id={kebabCase(
            `thumbnail-list-item-action-${data?.title || "unknown-title"}`,
          )}
          source={icons.meatballs}
          label={data?.actionButton?.label || "menu"}
          onPress={data?.actionButton?.onPress}
          {...(!data ? { state: "inactive" } : null)}
        />
      )}
    </Container>
  )
}
